import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import UIUtils, { DefaultSwitch, DefaultSwitch as Switch } from 'services/UIUtils';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultModal from 'components/Utils/DefaultModal';
import NewTaskButton from 'components/Tasks/NewTaskButton';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import PatientBadge from 'components/Patient/PatientBadge';
import DocumentAssign from 'components/HealthRecords/Documents/DocumentAssign';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import moment from 'moment-timezone';
import DocumentUtils from 'services/DocumentUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import CalendarUtils from 'services/CalendarUtils';
import './style.scss';
import EditDocument from 'components/HealthRecords/Documents/EditDocument';
import SignDocument from 'components/HealthRecords/Documents/SignDocument';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';
import Icon from 'components/Utils/NewIcon';
import StaffSearchField from 'components/Staff/StaffSearchField';
import PatientSearchField from 'components/Patient/PatientSearchField';
import DocumentBadge, { ThumbnailPreview } from 'components/HealthRecords/Documents/DocumentBadge';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { RemindersButton } from 'components/Patient/Reminders';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DiagnosticResults from 'components/HealthRecords/DiagnosticResults';
import IconButton from '@material-ui/core/IconButton';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import SendMultipleDocuments from 'components/HealthRecords/Documents/SendMultipleDocuments';
import NewReferralAdmin from 'components/HealthRecords/Documents/NewReferralAdmin';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { useAppSelector } from 'careand-redux/store';
import { useIsLoading, useNavStack } from 'careand-redux/slices/ui/hooks';
import ClinicSelector from 'components/Utils/ClinicSelector';
import StackItemsView from 'components/Common/StackItemsView';
import { REFERRAL_STATUS } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { ReferralActions } from 'scenes/HomeStaff/ReferralsDirectory';
import UnreadBadge from 'components/Common/UnreadBadge';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import NewDefaultAutoComplete from 'components/Utils/NewDefaultAutoComplete';
import ResponsiveColumns from 'components/ResponsiveColumns';
import CPPChat from 'components/Patient/CPPChat';
import TranslateDocumentButton from 'components/HealthRecords/Documents/TranslateDocumentButton';
import CPPUtils from 'services/CPPUtils';
import TranscribeImmunizationsButton from 'components/HealthRecords/Documents/TranscribeImmunizations';
import { ListItemIcon, Menu, ListSubheader } from '@material-ui/core';
import ListItemText from '@material-ui/core/ListItemText';
import { Comments } from 'components/Tasks/TaskDetails/view';
import { PERMISSIONS } from 'services/StaffUtils';
import PatientUtils from 'services/PatientUtils';
import MiscUtils from 'services/MiscUtils';
import RouteUtils from 'services/RouteUtils';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { usePathParams } from 'components/Navigation/NavStack';
import { v1 as uuidV1 } from 'uuid';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import ReactMarkdown from 'react-markdown';
import { useReduxForm, setFormValues } from 'hooks/useReduxForm';
import { MarkDocumentPendingPatientFillFillActionEnum } from '../../../../generated/api';

const dateFormat = 'Do MMM YYYY h:mm A';
const DEFAULT_ARRAY: any[] = [];
const DEFAULT_OBJECT = {};

const PREVENTATIVE_SCREENINGS: Record<string, any> = {
  Pap: { name: 'Pap', reminderName: 'Pap', search: ['pap'] },
  HPV: { name: 'HPV', reminderName: 'HPV', search: ['hpv'] },
  FIT: { name: 'FIT' },
  Mammogram: { name: 'Mammography', search: ['mammogra'] },
  Colonoscopy: { name: 'Colonoscopy', search: ['colonoscopy'] },
  'Fecal Immunochemical Test (FIT)': {
    name: 'Hemoccult',
    search: ['hemoccult', 'fit'],
  },
  'Bone Mineral Density': {
    name: 'Bone Mineral Density',
    search: ['bone mineral density', 'dexa', 'dxa', 'bmd'],
  },
};

function isPreventative(document: any) {
  return (
    !document.approved &&
    document.patient_id &&
    Object.keys(PREVENTATIVE_SCREENINGS).includes(document.title)
  );
}

function isResult(document: any) {
  return document.patient_id && document.document_type === DocumentUtils.DOCUMENT_TYPE.diagnostic;
}

function allowReferralUpdate(document: any) {
  return (
    !document.approved &&
    document.patient_id &&
    [
      DocumentUtils.DOCUMENT_TYPE.appointment_notification,
      DocumentUtils.DOCUMENT_TYPE.consult_note,
      DocumentUtils.DOCUMENT_TYPE.referral,
    ].includes(document.document_type)
  );
}

const DocumentDetailsView = (props: any) => {
  const dispatch = useAppDispatch();

  const [urlType, setUrlType] = useState<any>();
  const [showDetails, setShowDetails] = useState(!props.pinned || props.startShowingDetails);

  function getFile(dispatchOptions?: any) {
    setUrlType(undefined);
    return dispatch(
      api.Documents.getDocumentFile({ documentId: props.document?.id }),
      dispatchOptions,
    );
  }

  function getFileInfo() {
    return dispatch(api.Documents.getDocumentFileInfo({ documentId: props.document?.id }));
  }

  let className = 'document-details';
  if (!showDetails) className += ' hide-details';

  const isLocked = props.lockedToId && props.lockedToId !== props.ownId;
  let isReadOnly = props.disableEdit || isLocked;

  return (
    <div className={className}>
      {!showDetails && (
        <IconButton className="expand-button" onClick={() => setShowDetails(true)}>
          <Icon src="chevron-right" />
        </IconButton>
      )}
      {props.getHTML && (
        <DocumentTemplate
          overrideTitle={props.value.title}
          templateId={props.value.template_id}
          document={props.value}
          patient={props.patient}
          physician={props.physician}
          onSaveCompleted={undefined}
          autoSave={true}
          getHTML={props.getHTML}
        />
      )}
      {props.signModal && (
        <SignPdfModal
          {...props}
          url={urlType?.url}
          physician={props.ownPhysician || props.physician}
        />
      )}
      <ResponsiveColumns
        columns={[
          showDetails
            ? {
                name: props.openCPP ? 'CPP' : 'Details',
                element: props.openCPP ? (
                  <div className="document-cpp-wrapper">
                    {/*@ts-expect-error*/}
                    <CPPChat patient={props.patient} defaultHidePanel={true} />
                    <div className="document-details-main-screen">
                      <div className="details">
                        <CommentField {...props} />
                        <DefaultButton
                          name="Save"
                          icon="save"
                          className="save-button blue"
                          loading={props.loading}
                          onClick={() => props.updateDocument(props.value)}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="document-details-main-screen padded-box scroll-list">
                    {isReadOnly ? (
                      <ReadOnlyScreen
                        {...props}
                        urlType={urlType}
                        disableEdit={isLocked || props.disableEdit}
                        isLocked={isLocked}
                      />
                    ) : props.approve ? (
                      <ApproveScreen
                        {...props}
                        urlType={urlType}
                        isLocked={isLocked}
                        setShowDetails={setShowDetails}
                      />
                    ) : (
                      <EditScreen
                        {...props}
                        urlType={urlType}
                        isLocked={isLocked}
                        setShowDetails={setShowDetails}
                      />
                    )}
                    {!isLocked && props.extraButtons}
                  </div>
                ),
              }
            : undefined,
          props.previewElement || props.allowExpand === true
            ? {
                name: 'Preview',
                element: props.previewElement ? (
                  <div className="document-details-preview-screen">{props.previewElement}</div>
                ) : props.allowExpand === true ? (
                  <div className="document-details-preview-screen">
                    <LargeResourcePreview
                      key={props.previewKey}
                      autoLoad={props.autoLoad}
                      getFile={getFile}
                      getFileInfo={getFileInfo}
                      onLoad={(url: string, type: string) => setUrlType({ url, type })}
                      fitWidth={props.fitWidth}
                    />
                  </div>
                ) : undefined,
              }
            : undefined,
        ]}
      />
      <MultiplePatientWarning value={props.value} />
    </div>
  );
};

const ApproveScreen = (props: any) => {
  const classification = getDocumentClassification(props.document);
  const isIntern = props.ownPhysician?.intern_of;

  return (
    <div className="approve details">
      <TopButtons {...props} />

      <TitleField
        value={props.value}
        updateDocumentFields={props.updateDocumentFields}
        disableEdit={props.disableEdit}
        classificationResult={classification}
      />

      <div className="row info-wrapper">
        <div className="flex flex-col">
          <TypeField {...props} classificationResult={classification} />
          <DateField {...props} />
        </div>
        <div className="row">
          <FromField {...props} />
        </div>
      </div>

      <LinkDocumentModal {...props} />
      <LinkedDocumentBadge
        key={props.value.id}
        value={props.value}
        updateDocumentFields={props.updateDocumentFields}
        setLinkModal={props.setLinkModal}
      />

      <div className="row badges">
        {props.disablePatientBadge ? null : (
          <PatientSearchField
            onSelect={(patient: any) => {
              let patientId = patient ? patient.id : undefined;
              props.updateDocumentFields({ patient_id: patientId });
            }}
            patient={props.patient}
          />
        )}
        <StaffSearchField
          key={props.primaryPhysician?.id}
          prefill={props.primaryPhysician ? [props.primaryPhysician] : undefined}
          onSelect={(staff: any) => {
            let physicianId = staff ? staff.id : undefined;
            props.updateDocumentFields({ physician_id: physicianId });
          }}
          hideInactive={true}
          staff={props.physician}
          ignoreAdmins={true}
          ignoreClicks={true}
        />
      </div>

      <RedFlagsBanner value={props.value} />
      <AIAnalysis document={props.value} updateDocumentFields={props.updateDocumentFields} />

      {props.value.document_type === DocumentUtils.DOCUMENT_TYPE.pharmacy_authorization && (
        <RefillAnalysis document={props.value} showPrescribeButton closeModal={props.onClose} />
      )}

      {allowReferralUpdate(props.value) && (
        <AppointmentNotificationActions
          value={props.value}
          updateDocumentFields={props.updateDocumentFields}
        />
      )}

      {isPreventative(props.value) && (
        <PreventativeScreeningActions
          value={props.value}
          updateDocumentFields={props.updateDocumentFields}
        />
      )}

      {isResult(props.value) && (
        <RepeatableRequisitionOptions value={props.value} patient={props.patient} />
      )}

      <CommentField {...props} />
      {!props.isLocked && (
        <>
          <div className="inline-block">
            <DefaultButton
              name="Save Changes"
              icon="save"
              loading={props.loading}
              onClick={() => props.updateDocument(props.value)}
            />
          </div>
          {!isIntern && <ApproveButtons {...props} />}
        </>
      )}
    </div>
  );
};

const ReadOnlyScreen = (props: any) => (
  <div className="read-only details">
    <div className="document-header">
      {props.isLocked && (
        <div className="locked">
          <Icon src="lock" />
          <span>This document being edited by {props.lockedByName}</span>
        </div>
      )}
      {!!props.document.processing && (
        <div className="locked">
          <ProgressMessage />
          <span>Processing document, please wait</span>
        </div>
      )}
      <PinButton {...props} />
    </div>
    <TitleField
      value={props.value}
      updateDocumentFields={props.updateDocumentFields}
      disableEdit={props.disableEdit}
      style={props.style}
    />

    <div className="row info-wrapper">
      <div className="flex flex-col">
        <TypeField {...props} />
        <DateField {...props} />
      </div>
      <div className="row">
        <FromField {...props} />
      </div>

      <div className="row">
        {/*<StatusField {...props} />*/}
        <HideToggle {...props} />
      </div>

      <ArchiveToggle {...props} />

      <div className="row badges">
        {!props.disablePatientBadge && (
          <>
            <div className="small-title">Patient</div>
            <PatientBadge patient={props.patient} goToProfile={true} />
          </>
        )}
      </div>

      <CommentField {...props} isLocked={true} />
    </div>
  </div>
);

const PreventativeScreeningActions = ({ value, updateDocumentFields }: any) => {
  const dispatch = useAppDispatch();
  const { title } = value;
  const defaultOption = PREVENTATIVE_SCREENINGS[title];

  const defaultOptionName = defaultOption.name;

  const allHR = useAppSelector((state) => state.healthRecords.byId[value.patient_id]);

  let selectedOption = useMemo(() => {
    return Object.values(PREVENTATIVE_SCREENINGS).find(
      (option: any) => option.name === value.preventative_screening?.name,
    );
  }, [value.preventative_screening]);

  useEffect(() => {
    if (!allHR && value.patient_id) {
      dispatch(api.HealthRecords.getCPP({ patientId: value.patient_id }));
    }
  }, [dispatch, value.patient_id, allHR]);

  const healthRecords = allHR?.healthRecords;

  const matchingHRs = useMemo(() => {
    let searchArray = (selectedOption as any)?.search || [];
    if (searchArray.length === 0) return [];
    return healthRecords?.preventative_screenings?.filter((ps: any) => {
      if (CPPUtils.isHealthRecordItemEnabled(ps)) {
        let name = ps.value.name.toLowerCase().trim();
        return !!searchArray.find((search: string) => name.includes(search));
      }
    });
  }, [healthRecords, selectedOption]);

  useEffect(() => {
    if (defaultOptionName && !value.preventative_screening?.name) {
      updateValue('name', defaultOptionName, true);
    }
  }, [defaultOptionName, value.preventative_screening]);

  function updateValue(key: string, newValue: any, clear = false) {
    let preventative_screening: any = { ...(value.preventative_screening || {}) };
    let reminder_text = value.reminder_text;
    let reminder_date = value.reminder_date;
    if (clear) {
      preventative_screening = {};
      reminder_text = undefined;
      reminder_date = undefined;
    }
    if (key === 'name' && !newValue) {
      preventative_screening = undefined;
    } else {
      preventative_screening[key] = newValue;
    }
    updateDocumentFields({
      preventative_screening,
      reminder_text,
      reminder_date,
    });
  }

  return (
    <div className="preventative-screening-actions actions-wrapper">
      <div className="small-title">Preventive Screening Options</div>
      <div className="row">
        <DefaultSelectField
          label="Select a type"
          value={value.preventative_screening?.name || ''}
          onChange={(event: any) => {
            updateValue('name', event.target.value, true);
          }}
          inputElements={[
            <MenuItem key="none" value={undefined}>
              None
            </MenuItem>,
            ...Object.values(PREVENTATIVE_SCREENINGS).map(({ name }: any) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            )),
          ]}
          style={{ flexGrow: 1, width: 'unset' }}
        />
        {value.preventative_screening?.name && (
          <DefaultTextField
            label="Notes"
            style={{ flexGrow: 1 }}
            name="notes"
            value={value.preventative_screening?.notes || ''}
            onChange={(event: any) => {
              updateValue('notes', event.target.value);
            }}
          />
        )}
      </div>
      {matchingHRs && (
        <div key={(selectedOption as any)?.name}>
          {value.preventative_screening?.name &&
            matchingHRs.map((hr: any) => (
              <div className="row" key={hr?.value?.name}>
                <DefaultTextField
                  label={`Current Notes for ${hr.value.name} ${
                    hr?.history[0]?.date ? `(${CalendarUtils.format(hr?.history[0]?.date)})` : ''
                  }`}
                  value={hr?.value?.notes || 'None'}
                  disabled={true}
                  style={{ flexGrow: 1, width: 'unset' }}
                />
              </div>
            ))}
        </div>
      )}
      {value.preventative_screening?.name && (
        <DefaultSwitch
          name="reminder"
          padded={false}
          reversed={true}
          onChange={(_event: any, isInputChecked: boolean) => {
            updateDocumentFields({
              reminder_date: isInputChecked ? moment().unix() : undefined,
              reminder_text: isInputChecked
                ? value.reminder_text ||
                  `Repeat ${(selectedOption as any)?.reminderName || (selectedOption as any)?.name}`
                : undefined,
            });
          }}
          label="Add reminder"
          checked={!!value.reminder_date}
        />
      )}
      {value.preventative_screening?.name && value.reminder_date && (
        <div className="row">
          <DefaultDatePicker
            label="Reminder Date"
            name="reminder_date"
            placeholder="Reminder Date"
            value={value.reminder_date}
            style={{ flexGrow: 1 }}
            onChange={(_e: any, val: any) => updateDocumentFields({ reminder_date: val })}
            forceLabel
          />
          <DefaultTextField
            name="reminder_text"
            label="Reminder Text"
            value={value.reminder_text || ''}
            style={{ width: 'unset', flexGrow: 1 }}
            onChange={(event: any) => {
              updateDocumentFields({ reminder_text: event.target.value });
            }}
          />
        </div>
      )}
    </div>
  );
};

const RepeatableRequisitionOptions = ({ value, patient }: any) => {
  const dispatch = useAppDispatch();
  const [options, setOptions] = useState<any>({});

  const allHR = useAppSelector((state) => state.healthRecords.byId[value.patient_id]);

  useEffect(() => {
    if (!allHR && value.patient_id) {
      dispatch(api.HealthRecords.getCPP({ patientId: value.patient_id }));
    }
  }, [dispatch, value.patient_id, allHR]);

  const requisitions = _.orderBy(
    allHR?.documents.filter(
      (doc: any) =>
        doc.document_type === DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition && doc.template_id,
    ),
    'date',
    'desc',
  );

  const requisition = useMemo(() => {
    return requisitions.find(({ id }: any) => id === options.requisition_id);
  }, [options.requisition_id, requisitions]);

  return (
    <div className="repeatable-requisition-actions actions-wrapper">
      <div className="small-title">Reissue Requisition</div>
      <>
        <DefaultSelectField
          name="requisition"
          style={{
            ...UIUtils.SELECT_FIELD_STYLE,
            width: '250px',
            marginRight: '30px',
          }}
          label="Previous Requisition"
          value={options.requisition_id}
          onChange={(event: any) => {
            setOptions((prevState: any) => ({
              ...prevState,
              requisition_id: event.target.value,
            }));
          }}
          inputElements={Object.entries(_.groupBy(requisitions, 'title'))
            .map(([title, documents]) => [
              <ListSubheader style={{ background: 'white' }} key={`header-${title}`}>
                {title}
              </ListSubheader>,
              ...(documents as any[]).map((doc: any) => (
                <MenuItem className="overflow-visible" key={doc.id} value={doc.id}>
                  <>
                    <ThumbnailPreview
                      key={doc.thumbnail}
                      url={doc.thumbnail}
                      width="auto"
                      thumbnailStyle={{
                        position: 'fixed',
                        top: '0',
                        right: '0',
                        left: 'unset',
                        height: '100vh',
                        width: 'auto',
                      }}
                    />
                    <span className="flex-grow ml-3">{CalendarUtils.format(doc.date)}</span>
                  </>
                </MenuItem>
              )),
            ])
            .flat()}
        />
        <DefaultButton
          name="Open Requisition"
          hide={!options.requisition_id}
          onClick={() => setOptions((prevState: any) => ({ ...prevState, openTemplate: true }))}
        />
        {requisition && options.openTemplate && (
          <DocumentTemplateModal
            documentTemplateModal={true}
            patient={patient}
            templateId={requisition.template_id}
            prefilledValues={JSON.parse(requisition.template_metadata || '{}')}
            defaultSavedDocumentCategory={DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition}
            setDocumentTemplateModal={(openTemplate: boolean) =>
              setOptions((prevState: any) => ({ ...prevState, openTemplate }))
            }
            onSaveCompleted={() => {
              setOptions((prevState: any) => ({
                ...prevState,
                openTemplate: false,
              }));
            }}
            onClose={() =>
              setOptions((prevState: any) => ({
                ...prevState,
                openTemplate: false,
              }))
            }
          />
        )}
      </>
    </div>
  );
};

// Staff-only document-level safety banner. Deliberately NOT embedded in
// AIAnalysis: that component early-returns null when document.analysis?.analysis
// is empty, which would suppress the banner on a flags-only multi-patient PDF
// that carries no clinical analysis text. red_flags is read top-level and gated
// independently on a broad-staff check (these are safety alerts, so wider than
// AIAnalysis's physician&&!approved gate). Server-side filterDocumentData is the
// real PHI/visibility boundary (red_flags is stripped for patients); this gate
// is UX only.
const RedFlagsBanner = ({ value }: any) => {
  const role = useAppSelector((state) => state.authentication.role);
  const permissions = useAppSelector((state) => state.authentication.permissions);

  const redFlags = value?.red_flags;
  const isStaff =
    role === 'physician' ||
    role === 'admin' ||
    permissions.includes(PERMISSIONS.super_admin);

  if (!isStaff || !Array.isArray(redFlags) || redFlags.length === 0) {
    return null;
  }

  return (
    <div className="red-flags-banner">
      {redFlags.map((flag: string, i: number) => (
        <div className="red-flag" key={i}>
          <Icon fontAwesome="triangle-exclamation" width="16px" height="16px" />
          <span>{flag}</span>
        </div>
      ))}
    </div>
  );
};

const AIAnalysis = ({ document, updateDocumentFields }: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const aiAnalysis = document.analysis?.analysis;
  const role = useAppSelector((state) => state.authentication.role);
  const permissions = useAppSelector((state) => state.authentication.permissions);

  const tldr = useMemo(() => {
    return document.analysis?.tldr?.replace(/ —/g, ', ');
  }, [document]);

  const canView =
    aiAnalysis &&
    ((role === 'physician' && !document.approved) || permissions.includes(PERMISSIONS.super_admin));

  if (!canView) {
    return null;
  }

  async function copyToClipboard() {
    if (tldr) {
      await navigator.clipboard.writeText(tldr);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function addAsPatientNote() {
    if (tldr && document.id && updateDocumentFields) {
      const currentNotes = document.comments || '';
      const newNotes = currentNotes ? `${currentNotes}\n\n${tldr}` : tldr;
      updateDocumentFields({ comments: newNotes });
    }
  }

  return (
    <div className="ai-analysis">
      <div className="ai-analysis-header" onClick={() => setIsExpanded(!isExpanded)}>
        <span className="small-title">AI Analysis</span>
        <Icon src={isExpanded ? 'chevron-up' : 'chevron-down'} height="18px" width="18px" />
      </div>
      {tldr && (
        <div className="ai-tldr">
          <div className="tldr-header">
            <span className="small-title">Summary</span>
            <div className="tldr-actions">
              <IconButton onClick={copyToClipboard}>
                <Icon
                  src="copy"
                  height="18px"
                  className="blue"
                  alt={copied ? 'Copied!' : 'Copy'}
                  showTooltip={true}
                />
              </IconButton>
              <IconButton onClick={addAsPatientNote}>
                <Icon
                  src="note-medical"
                  height="18px"
                  className="blue"
                  alt="Add as Patient Note"
                  showTooltip={true}
                />
              </IconButton>
            </div>
          </div>
          <div className="tldr-content">{tldr}</div>
        </div>
      )}
      <div className="ai-analysis-content" data-expanded={`${isExpanded}`}>
        <ReactMarkdown>{aiAnalysis}</ReactMarkdown>
      </div>
    </div>
  );
};

const AppointmentNotificationActions = ({ value, updateDocumentFields }: any) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (value.patient_id) {
      dispatch(api.HealthRecords.getCPP({ patientId: value.patient_id }));
    }
  }, [dispatch, value.patient_id]);

  const isLoading = useIsLoading(api.HealthRecords.getCPP);
  const allHR = useAppSelector((state) => state.healthRecords.byId[value.patient_id]);

  const allReferrals = useMemo(() => {
    return (allHR?.documents || []).filter(
      ({ referral_status }: any) =>
        !!referral_status &&
        referral_status !== REFERRAL_STATUS.completed &&
        referral_status !== REFERRAL_STATUS.rejected,
    );
  }, [allHR]);

  const selectedDocumentId = value.linked_documents?.[0];
  const selectedDocument = useMemo(() => {
    return allReferrals.find((referral: any) => referral.id === selectedDocumentId);
  }, [allReferrals, selectedDocumentId]);
  const selectedStatus = value.change_linked_referral_status;

  const shouldNotifyDefault =
    value.document_type === DocumentUtils.DOCUMENT_TYPE.appointment_notification;

  useEffect(() => {
    if (shouldNotifyDefault) {
      updateDocumentFields({
        send_to_patient: true,
      });
    }
  }, [shouldNotifyDefault]);

  return (
    <div className="appointment-notification-actions actions-wrapper">
      <div className="small-title">Pending Referrals</div>
      {isLoading ? (
        <ProgressMessage size={15} />
      ) : (
        <>
          <div className="row">
            {allReferrals.length === 0 && <span>No pending referrals found</span>}
            {allReferrals.length > 0 && (
              <DefaultSelectField
                key={selectedDocumentId}
                id="referral-select"
                label="Select a referral"
                value={selectedDocumentId}
                onChange={(event: any) => {
                  let linked_documents = event.target.value ? [event.target.value] : [];
                  updateDocumentFields({
                    linked_documents,
                    change_linked_referral_status: undefined,
                  });
                }}
                inputElements={[
                  <MenuItem key="none" value={undefined}>
                    None
                  </MenuItem>,
                  ...allReferrals.map((referral: any) => (
                    <MenuItem key={referral.id} value={referral.id}>
                      {referral.title} ({CalendarUtils.format(referral.date)},{' '}
                      {referral.referral_status})
                    </MenuItem>
                  )),
                ]}
                style={{ width: '100%' }}
              />
            )}
          </div>
          {selectedDocument && (
            <div className="row">
              <DefaultSelectField
                key={selectedStatus}
                id="referral-status-select"
                label="Select a new referral status"
                value={selectedStatus}
                onChange={(event: any) => {
                  updateDocumentFields({
                    change_linked_referral_status: event.target.value,
                  });
                }}
                inputElements={[
                  <MenuItem
                    key={REFERRAL_STATUS.pending_reply}
                    value={REFERRAL_STATUS.pending_reply}
                  >
                    Pending Reply
                  </MenuItem>,
                  <MenuItem key={REFERRAL_STATUS.completed} value={REFERRAL_STATUS.completed}>
                    Approved
                  </MenuItem>,
                  <MenuItem key={REFERRAL_STATUS.rejected} value={REFERRAL_STATUS.rejected}>
                    Rejected
                  </MenuItem>,
                ]}
                style={{ width: '100%' }}
              />
            </div>
          )}
          {shouldNotifyDefault && (
            <DefaultSwitch
              name="send_to_patient"
              padded={false}
              reversed={true}
              onChange={(_event: any, isInputChecked: boolean) => {
                updateDocumentFields({
                  send_to_patient: isInputChecked,
                });
              }}
              label="Send appointment notification to patient"
              checked={!!value.send_to_patient}
            />
          )}
          {selectedDocument && selectedStatus && (
            <span className="disclaimer">
              Updates to referral will take effect once the document is approved
            </span>
          )}
        </>
      )}
    </div>
  );
};

// Migrated from redux-form: uses useReduxForm instead of compose(connect(), reduxForm())
const RefillAnalysis = ({ document, showPrescribeButton, closeModal }: any) => {
  const dispatch = useAppDispatch();
  const analysis = document.pharmacy_authorization_analysis;
  const [open, setOpen] = useState<string[]>([]);
  const nav = useNavStack(Stacks.Master);
  const bubbleId = (usePathParams() as any)?.bubbleId;
  const [showRejectPopup, setShowRejectPopup] = useState(false);

  // Build the form name the same way as the old mapStateToProps
  const formName = CPPUtils.getFormName('prescription', document.patient_id);

  // useReduxForm replaces compose(connect(), reduxForm({ destroyOnUnmount: false }))
  const { reset: resetForm } = useReduxForm(formName, {
    destroyOnUnmount: false,
    defaultValues: {},
  });

  useEffect(() => {
    if (analysis?.medications) {
      dispatch(api.HealthRecords.getCPP({ patientId: document.patient_id }));
    }
  }, [analysis]);

  function toggleOpen(name: string) {
    setOpen((prev) => {
      return prev.includes(name) ? prev.filter((it) => it !== name) : [...prev, name];
    });
  }

  function openPrescription() {
    if (analysis?.generated_prescription?.medications) {
      // Replace MiscUtils.changeForm(props, {...}) with dispatching setFormValues directly
      dispatch(
        setFormValues({
          formName,
          values: {
            medications: analysis.generated_prescription.medications,
            originalPharmacy: analysis.generated_prescription.pharmacy,
          },
        }),
      );
    }
    const bubble = bubbleId ? { id: bubbleId } : undefined;
    const route = RouteUtils.getCPPRoute(document.patient_id, 'prescription', bubble)
      .split('//')
      .join('/');
    nav.push(route);
  }

  if (showRejectPopup) {
    return (
      <div className="refill-analysis bordered">
        <span className="small-title mb-1">Refill Request Analysis</span>
        <PharmacyRefillRequestRejectionPopup
          fax={analysis?.pharmacy_reply_to}
          isOpen={showRejectPopup}
          onRequestClose={() => setShowRejectPopup(false)}
          document={document}
        />
      </div>
    );
  }

  return (
    <div className="refill-analysis">
      <span className="small-title mb-1">Refill Request Analysis</span>
      <div className="flex flex-col gap-2">
        {analysis?.medications?.map((med: any) => {
          const isOpen = open.includes(med.medication_name);
          return (
            <div
              className="med-analysis"
              key={med.medication_name}
              data-open={`${isOpen}`}
              data-refill={`${med.allow_refill}`}
              onClick={() => toggleOpen(med.medication_name)}
            >
              <div className="med-name">
                <Icon
                  src={med.allow_refill ? 'circle-check' : 'circle-exclamation'}
                  height="18px"
                  width="18px"
                />
                {med.medication_name}
                <Icon src={!isOpen ? 'chevron-down' : 'chevron-up'} height="18px" width="18px" />
              </div>
              <div className="med-analysis-content">{med.analysis}</div>
            </div>
          );
        })}
        <div className="buttons gap-2 flex">
          <DefaultButton name="Reject" className="red" onClick={() => setShowRejectPopup(true)} />
          {showPrescribeButton && (
            <DefaultButton
              name="Start Prescription"
              className="blue"
              onClick={() => {
                dispatch(
                  uiActions.addPinnedDocument({
                    patientId: document.patient_id,
                    type: 'document',
                    id: document.id,
                    startShowingDetails: true,
                  }),
                );
                closeModal?.();
                openPrescription();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const PharmacyRefillRequestRejectionPopup = ({ document, fax, onRequestClose }: any) => {
  const dispatch = useAppDispatch();
  const [msg, setMsg] = useState<string | undefined>();
  const stamping = useIsLoading(api.Documents.stampDocument);
  const [faxing, setFaxing] = useState(false);
  const [replyFax, setReplyFax] = useState(fax);

  function getStamp(text: string) {
    const measurementCanvas = window.document.createElement('canvas');
    const ctx = measurementCanvas.getContext('2d')!;
    const maxWidth = 600;
    const padding = 15;
    const fontSize = 20;
    ctx.font = `${fontSize}px verdana`;

    const initialLines = text.split('\n');
    let lines: string[] = [];

    initialLines.forEach((line) => {
      const words = line.split(/\s+/);
      let currentLine = words[0]!;

      for (let i = 1; i < words.length; i++) {
        const word = words[i]!;
        const testLine = currentLine + ' ' + word;
        const { width: testWidth } = ctx.measureText(testLine);

        if (testWidth > maxWidth - 2 * padding) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);
    });

    const lineHeight = fontSize + 5;
    const requiredHeight = lines.length * lineHeight + 2 * padding;

    const canvas = window.document.createElement('canvas');
    canvas.width = maxWidth;
    canvas.height = requiredHeight;
    const renderCtx = canvas.getContext('2d')!;

    renderCtx.lineWidth = 5;
    renderCtx.strokeStyle = '#2693d1';
    renderCtx.fillStyle = 'white';
    (renderCtx as any).roundRect(0, 0, maxWidth, requiredHeight, 15);
    renderCtx.fill();
    renderCtx.stroke();

    renderCtx.fillStyle = 'black';
    renderCtx.font = `${fontSize}px verdana`;

    lines.forEach((line, index) => {
      renderCtx.fillText(line, padding, padding + (index + 1) * lineHeight);
    });

    const pngUrl = canvas.toDataURL();
    return pngUrl;
  }

  async function faxDocument() {
    const docFile = await dispatch(api.Documents.getDocumentFile({ documentId: document.id }), {
      errorTitle: 'Resource Preview',
    });

    if (!docFile) {
      return;
    }

    const url = MiscUtils.getBlobUrl(docFile);

    if (!url) {
      return;
    }

    const filename = uuidV1() + '.pdf';
    const file = await MiscUtils.urlToFile(url, 'application/pdf', filename);

    if (!file) {
      return;
    }

    await dispatch(
      api.Fax.sendFileFax({
        file,
        fax: replyFax,
        title: document.title,
        patientId: document.patient_id,
        ignoreCoverPage: false,
      }),
      { errorTitle: 'Send Fax' },
    );
  }

  async function stampDocument(text: string) {
    const stamp = getStamp(text);
    if (!stamp) {
      return;
    }
    const args = {
      page: 1,
      image_base64: stamp,
      x_percent: 0.3,
      y_percent: 0.5,
      width_percent: 0.4,
    };
    await dispatch(
      api.Documents.stampDocument({ documentId: document.id, documentStamps: [args] }),
    );
    onRequestClose();
  }

  return (
    <div className="p-2 rejection-popup">
      <DefaultTextField
        label="Fax"
        value={replyFax}
        onChange={(ev: any) => setReplyFax(ev.target.value)}
      />
      <div className="small-title">Message</div>
      <div className="relative">
        <TextareaAutosize value={msg} onChange={(ev: any) => setMsg(ev.target.value)} />
      </div>
      <div className="flex gap-2">
        <DefaultButton
          disabled={stamping || faxing}
          name="Cancel"
          className="red"
          onClick={onRequestClose}
        />
        <DefaultButton
          disabled={stamping || faxing}
          loading={stamping}
          name="Save"
          onClick={async () => {
            await stampDocument(msg!);
            onRequestClose();
          }}
        />
        <DefaultButton
          loading={faxing}
          disabled={stamping}
          name="Save & Fax"
          className="blue"
          onClick={async () => {
            setFaxing(true);
            await stampDocument(msg!);
            await faxDocument().finally(() => setFaxing(false));
          }}
        />
      </div>
    </div>
  );
};

const MedicationStack = ({ value, updateDocumentFields }: any) => {
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(api.HealthRecords.getCPP);
  const [search, setSearch] = useState('');
  const allHR = useAppSelector((state) => state.healthRecords.byId[value.patient_id]);

  const allMedications = allHR?.healthRecords?.medications || DEFAULT_ARRAY;

  const medications = value.medications || DEFAULT_ARRAY;

  useEffect(() => {
    if (value.patient_id && allMedications.length === 0) {
      dispatch(api.HealthRecords.getCPP({ patientId: value.patient_id }));
    }
  }, [dispatch, value.patient_id]);

  const dataSource = useMemo(() => {
    let currentMedications = medications.map((medication: any) => medication.name);
    return allMedications
      .filter((medication: any) => !currentMedications.includes(medication.value.name))
      .map((medication: any) => medication.value.name)
      .sort((a: string, b: string) => a.localeCompare(b));
  }, [allMedications, medications]);

  function onChange(val: string) {
    let medication = allMedications.find((medication: any) => medication.value.name === val);
    medication = medication?.value?.prescription_medication || {
      name: medication?.value?.name,
    };
    if (medication) updateDocumentFields({ medications: [...medications, medication] });
  }

  function deleteMedication(medication: any) {
    updateDocumentFields({
      medications: medications.filter((m: any) => m.name !== medication.name),
    });
  }

  return (
    <>
      <span className="small-title">Medications Refilled</span>
      <div className="medications">
        {!isLoading && !value.approved && (
          <DefaultAutoComplete
            /*@ts-ignore*/
            id="medication-input"
            style={{ width: '100%', marginBottom: '10px' }}
            placeholder="Search patient medications"
            input={{
              value: search,
              onChange: (val: string) => onChange(val),
            }}
            onUpdateInput={setSearch}
            dataSource={[...dataSource, ...dataSource]}
            popOut={true}
            maxMenuHeight={150}
          />
        )}
        {isLoading && <ProgressMessage show={isLoading} />}
        {!isLoading && (
          <StackItemsView
            stack={medications}
            describeItem={(medication: any) => medication.name}
            onDeleteItem={!value.approved ? deleteMedication : undefined}
            /*@ts-ignore*/
            hideStatus={true}
          />
        )}
        {medications.length === 0 && !isLoading && value.approved && (
          <span>No medications approved</span>
        )}
      </div>
    </>
  );
};

const PinButton = ({ document, allowPin, onClose }: any) => {
  const dispatch = useAppDispatch();

  async function onPin() {
    dispatch(
      uiActions.addPinnedDocument({
        patientId: document.patient_id,
        type: 'document',
        id: document.id,
      }),
    );
    onClose?.();
  }

  return (
    allowPin === true && (
      <IconButton className="ml-2" onClick={onPin}>
        <Icon height="20px" width="20px" src="thumbtack" alt="Pin to Side" showTooltip={true} />
      </IconButton>
    )
  );
};

const InsightsButtons = ({ value, document, isAdmin, updateDocumentFields }: any) => {
  const dispatch = useAppDispatch();
  const permissions = useAppSelector((state) => state.authentication.permissions);
  const patient = useAppSelector((state) => state.patients.byId[value.patient_id]);
  const [loading, setLoading] = useState<string | undefined>();

  async function onClickExtract() {
    setLoading('extract');
    let response = await dispatch(
      api.Documents.extractDocumentHistoricalResults({
        documentId: value.id,
      }),
    );
    if (response) {
      updateDocumentFields(response);
    }
    dispatch(api.Documents.getDocumentFlags({ documentId: value.id }));
    setLoading(undefined);
  }

  async function onClick() {
    dispatch(
      uiActions.addAssistant({
        setActive: true,
        firstMessage: {
          type: 'promptData',
          savedPromptData: {
            name: 'Document Analysis',
            savedPromptRequest: {
              fields: [],
              patient_id: document.patient_id,
              document_id: document.id,
            },
          },
        },
        subject: PatientUtils.getName(patient, undefined, false, false) + ' - ' + document.title,
      }),
    );
  }

  const showExtractButton =
    document.id &&
    document.document_type === DocumentUtils.DOCUMENT_TYPE.diagnostic &&
    (!document.results_text || permissions.includes(PERMISSIONS.super_admin)) &&
    document.patient_id;

  const showAnalysisButton =
    value.id && document.patient_id && (!isAdmin || permissions.includes(PERMISSIONS.super_admin));

  return (
    <>
      {showExtractButton && (
        <IconButton className="ml-2" onClick={onClickExtract}>
          <Icon
            src="chart-line"
            height="20px"
            width="20px"
            alt="Extract Lab Results"
            showTooltip={true}
            loading={loading === 'extract'}
          />
        </IconButton>
      )}
      {showAnalysisButton && (
        <IconButton className="ml-2" onClick={onClick}>
          <Icon
            src="microchip-ai"
            height="20px"
            width="20px"
            alt="AI Document Analysis"
            showTooltip={true}
          />
        </IconButton>
      )}
    </>
  );
};

const DecryptButton = ({ document, saveDocument, changePreviewKey }: any) => {
  const isLoading = useIsLoading(api.Documents.decryptDocument);
  const dispatch = useAppDispatch();
  const [confirm, setConfirm] = useState(false);

  async function decryptDocument(password: string) {
    let result = await dispatch(
      api.Documents.decryptDocument({
        documentId: document.id,
        requiredStringResource: { value: password },
      }),
      {
        title: 'Remove Password',
        successText: 'Password removed successfully',
      },
    );
    if (result) {
      saveDocument(result);
      changePreviewKey();
    }
  }

  return (
    document.has_password && (
      <>
        {confirm && (
          <ConfirmationDialog
            title="Remove Password"
            open={confirm}
            message="Please type the document's password"
            onConfirm={(_event: any, form: any) => decryptDocument(form['Name'])}
            form={[
              {
                type: 'textField',
                label: 'Name',
              },
            ]}
            onCancel={() => setConfirm(false)}
            loadingState={isLoading}
          />
        )}
        <IconButton className="ml-2" onClick={() => setConfirm(true)}>
          <Icon
            src="key"
            height="20px"
            width="20px"
            alt="Remove Password"
            showTooltip={true}
            loading={isLoading}
          />
        </IconButton>
      </>
    )
  );
};

const DeleteButton = ({ document, onClose, allowDelete }: any) => {
  const isLoading = useIsLoading(api.Documents.deleteDocument);
  const dispatch = useAppDispatch();
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function onDelete() {
    const { isSuccess } = await dispatch(
      api.Documents.deleteDocument({ documentId: document.id }),
      {
        errorTitle: 'Delete Document',
        meta: true,
      },
    );
    if (isSuccess && onClose) onClose();
  }

  return (
    allowDelete === true && (
      <>
        <MenuItem onClick={() => setConfirmDelete(true)}>
          <ListItemIcon>
            <Icon src="trash" height="20px" />
          </ListItemIcon>
          <ListItemText primary="Delete Document" />
        </MenuItem>
        {confirmDelete && (
          <ConfirmationDialog
            title="Delete Document"
            message="This will delete the document. Proceed?"
            open={confirmDelete}
            onConfirm={onDelete}
            onCancel={() => setConfirmDelete(false)}
            loadingState={isLoading}
          />
        )}
      </>
    )
  );
};

const TopButtons = (props: any) => {
  const dispatch = useAppDispatch();
  const [menuAnchor, setMenuAnchor] = useState<any>();
  const [fullscreen, setFullscreen] = useState(false);

  function getFile(dispatchOptions?: any) {
    return dispatch(
      api.Documents.getDocumentFile({ documentId: props.document?.id }),
      dispatchOptions,
    );
  }

  function getFileInfo() {
    return dispatch(api.Documents.getDocumentFileInfo({ documentId: props.document?.id }));
  }

  return (
    <div className="document-header">
      <div className="desktop-only">
        <div className="expand">
          <div className="header-button">
            <IconButton onClick={() => props.setShowDetails(false)}>
              <Icon
                src="chevron-left"
                alt="Hide Details"
                showTooltip={true}
                tooltipPlace="right"
                height="20px"
              />
            </IconButton>
          </div>
          <div className="header-button">
            {fullscreen && (
              <DefaultModal
                className="fullscreen-modal"
                key="add-document-modal"
                title="Receipt"
                wrap={true}
                padding={false}
                outerStyle={{ zIndex: 1 }}
                content={
                  <LargeResourcePreview
                    autoLoad={true}
                    getFile={getFile}
                    getFileInfo={getFileInfo}
                    fitWidth={true}
                    url={props.urlType?.url}
                    type={props.urlType?.type}
                  />
                }
                isOpen={fullscreen}
                onRequestClose={() => setFullscreen(false)}
              />
            )}
            {props.document.id && (
              <IconButton onClick={() => setFullscreen(true)}>
                <Icon
                  src="expand"
                  alt="Fullscreen Mode"
                  showTooltip={true}
                  tooltipPlace="right"
                  height="20px"
                />
              </IconButton>
            )}
          </div>
        </div>
      </div>
      <InsightsButtons {...props} />
      <DecryptButton
        document={props.document}
        saveDocument={props.saveDocument}
        changePreviewKey={props.changePreviewKey}
      />

      <EditPDFButton {...props} />
      <SignPDFButton {...props} />

      <SendDocument
        disableSend={props.disableSend}
        document={props.document}
        patient={props.patient}
      />
      <PinButton document={props.document} allowPin={props.allowPin} onClose={props.onClose} />

      <TranscribeImmunizationsButton document={props.document} />
      <NewTaskButton
        patient={props.patient}
        hideName={true}
        task={{ documents: [props.document.id] }}
      />
      {!props.isAdmin && (
        <RemindersButton
          user={props.patient}
          userRole="patient"
          width="20px"
          button={true}
          className="grey button-reminders"
          document={props.document}
        />
      )}

      <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
        <Icon
          src="ellipsis-vertical"
          height="20px"
          width="20px"
          alt="More Options"
          showTooltip={true}
        />
      </IconButton>

      {props.showDocumentEdit && (
        <DefaultModal
          className="fullscreen-modal"
          key="edit-document-modal"
          title="Document Details"
          content={
            <EditDocument
              /*@ts-ignore*/
              url={props.urlType?.url}
              document={props.document}
              onClose={(doc: any) => {
                if (doc && doc.id) {
                  props.saveDocument(doc);
                } else {
                  if (props.onClose) props.onClose();
                }
                props.openDocumentEdit(false);
              }}
            />
          }
          isOpen={props.showDocumentEdit}
          onRequestClose={() => props.openDocumentEdit(false)}
        />
      )}

      {menuAnchor && (
        <Menu
          anchorEl={menuAnchor}
          keepMounted
          disablePortal={true}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(undefined)}
        >
          <SplitPDFButton {...props} onMenuClose={() => setMenuAnchor(undefined)} />
          <ConvertToPDFButton {...props} />
          <SendDocumentForPatientToFill {...props} />
          <CompressPdfButton {...props} />
          <TranslateDocumentButton
            document={props.document}
            onClose={() => setMenuAnchor(undefined)}
          />
          <DeleteButton
            onClose={props.onClose}
            allowDelete={props.allowDelete}
            document={props.document}
          />
        </Menu>
      )}
    </div>
  );
};

function CompressPdfButton(props: any) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const hasFilename = !!props.document.filename;
  const isPDF = hasFilename && props.document.filename.endsWith('.pdf');
  const showCompressButton = hasFilename && isPDF;

  if (!showCompressButton) {
    return null;
  }

  async function compress() {
    setLoading(true);

    const before = await dispatch(
      api.Documents.getDocumentFileInfo({ documentId: props.document.id }),
    );
    const result = await dispatch(
      api.Documents.compressDocument({ documentId: props.document.id }),
    );
    const after = await dispatch(
      api.Documents.getDocumentFileInfo({ documentId: props.document.id }),
    );
    const diff =
      (after as any)?.size && (before as any)?.size
        ? (((before as any).size - (after as any).size) / (before as any).size) * 100
        : undefined;

    if (result) {
      props.saveDocument(result);
      props.changePreviewKey();
    }

    if (diff && diff > 0.05) {
      MiscUtils.showToastrInfo({
        title: 'File compressed',
        message: `File compressed from ${byteToMegabyte(
          (before as any).size,
        )}MB to ${byteToMegabyte((after as any).size)}MB (${diff}% reduction)`,
      });
    } else {
      MiscUtils.showToastrError({ title: 'File compression failed' } as any);
    }

    setLoading(false);
  }

  return (
    <MenuItem onClick={compress}>
      <ListItemIcon>
        <Icon src="file-zipper" height="20px" loading={loading} />
      </ListItemIcon>
      <ListItemText primary="Compress Document" />
    </MenuItem>
  );
}

const MultiplePatientWarning = ({ value }: any) => {
  const [showPatientWarning, setShowPatientWarning] = useState(false);
  const multiplePatients =
    _.uniq((value.ocr_patient_ids || []).map(({ id }: any) => id)).length > 1;

  useEffect(() => {
    if (multiplePatients && value.patient_id) {
      setShowPatientWarning(true);
    }
  }, [value.patient_id, multiplePatients]);

  return (
    <ConfirmationDialog
      open={showPatientWarning}
      onClose={() => setShowPatientWarning(false)}
      title="WARNING: Multiple Patients Detected"
      message="A patient was assigned but multiple patients have been identified in the document. Please double-check before saving."
      onConfirm={() => setShowPatientWarning(false)}
    />
  );
};

const EditScreen = (props: any) => {
  const classification = getDocumentClassification(props.value);
  return (
    <div className="edit details">
      <TopButtons {...props} />

      <TitleField
        value={props.value}
        updateDocumentFields={props.updateDocumentFields}
        disableEdit={props.disableEdit}
        classificationResult={classification}
      />

      <div className="row info-wrapper">
        <div className="flex flex-col">
          <TypeField {...props} style={{ width: '100%' }} classificationResult={classification} />
        </div>

        <div className="row">
          <DateField {...props} style={{ marginRight: '10px' }} />
          {/*<StatusField {...props} />*/}
        </div>
        <div className="row">
          <FromField {...props} />
        </div>
        {!props.value.approved && (
          <div className="row">
            <ClinicSelector
              style={{ width: '100%', marginRight: '20px' }}
              value={props.value.clinic_id}
              setValue={(value: any) => props.updateDocumentFields({ clinic_id: value })}
            />
          </div>
        )}
        <TrackReferralToggle {...props} />
        <div className="row">
          <div className="inline-block">
            <HideToggle {...props} />
          </div>
          <div className="row">
            <div className="inline-block">
              {(!props.value.approved || props.document.archived) && <ArchiveToggle {...props} />}
            </div>
          </div>
          <LinkDocumentModal {...props} />
          <LinkedDocumentBadge
            key={props.value.id}
            value={props.value}
            updateDocumentFields={props.updateDocumentFields}
            setLinkModal={props.setLinkModal}
          />
        </div>

        <PaymentFields
          value={props.value}
          updateDocumentFields={props.updateDocumentFields}
          chargingPatient={props.chargingPatient}
          chargePatient={props.chargePatient}
        />

        <div className="row badges">
          {props.disablePatientBadge ? null : (
            <React.Fragment>
              {!props.isPractitioner && (
                <PatientSearchField
                  onSelect={(patient: any) => {
                    let patientId = patient ? patient.id : undefined;
                    props.updateDocumentFields({ patient_id: patientId });
                  }}
                  patient={props.patient}
                />
              )}
              {props.isPractitioner && (
                <>
                  <div className="small-title">Patient</div>
                  <PatientBadge patient={props.patient} goToProfile={true} />
                </>
              )}
            </React.Fragment>
          )}
          <StaffSearchField
            key={props.primaryPhysician?.id}
            prefill={props.primaryPhysician ? [props.primaryPhysician] : undefined}
            onSelect={(staff: any) => {
              let physicianId = staff ? staff.id : undefined;
              let data: any = { physician_id: physicianId };
              if (staff) data.clinic_id = staff.clinic_id;
              props.updateDocumentFields(data);
            }}
            hideInactive={true}
            staff={props.physician}
            ignoreAdmins={true}
            ignoreClicks={true}
          />
        </div>

        {allowReferralUpdate(props.value) && (
          <AppointmentNotificationActions
            value={props.value}
            updateDocumentFields={props.updateDocumentFields}
          />
        )}

        {isPreventative(props.value) && (
          <PreventativeScreeningActions
            value={props.value}
            updateDocumentFields={props.updateDocumentFields}
          />
        )}

        {isResult(props.value) && (
          <RepeatableRequisitionOptions value={props.value} patient={props.patient} />
        )}

        <RedFlagsBanner value={props.value} />
        <AIAnalysis document={props.value} updateDocumentFields={props.updateDocumentFields} />

        <CommentField {...props} />
        <NewReferralButton {...props} />
        <SaveButtons {...props} />
        {props.allowAdminApprove && (
          <>
            <br />
            <ApproveButtons {...props} />
          </>
        )}
      </div>
    </div>
  );
};

const PaymentFields = ({ value, updateDocumentFields, chargingPatient, chargePatient }: any) => {
  const defaultShowPayment = value.fee;

  return (
    !!value.fee && (
      <UncontrolledCollapse title="Payment Options" initialValue={!defaultShowPayment}>
        <div className="row">
          <DefaultTextField
            name="fee"
            label={value.paid ? 'Paid Fee ($)' : 'Unpaid Fee ($)'}
            style={{ width: '40%' }}
            value={value.fee || ''}
            disabled={true}
            onChange={(event: any) => updateDocumentFields({ fee: event.target.value })}
          />
          <div className="ml-9 mt-2">
            <ConfirmationButton
              name="Charge Patient"
              icon="credit-card"
              hide={!!value.paid}
              disabled={!value.fee}
              style={{ position: 'relative', top: '13px' }}
              className="ml-2 blue"
              loading={chargingPatient}
              onClick={chargePatient}
              confirmation={{
                message: `This will charge the patient's credit card for a total of $${(
                  parseFloat(value.fee) || 0.0
                ).toFixed(2)}${value.fee_hst ? ' + HST' : ''}. Are you sure?`,
              }}
            />
          </div>
        </div>
      </UncontrolledCollapse>
    )
  );
};

export const NewReferralButton = ({ document, patient, onClose }: any) => {
  const [referralModal, setReferralModal] = useState(false);
  return (
    !!patient &&
    !!document.referral && (
      <div className="inline-block">
        <DefaultButton
          name="New Referral"
          onClick={() => setReferralModal(true)}
          icon="user-doctor"
        />
        {referralModal && patient && (
          <DefaultModal
            wrap={true}
            title="New Referral"
            className="fit-modal new-referral-modal"
            padding={false}
            content={
              <NewReferralAdmin
                patient={patient}
                document={document}
                onClose={() => setReferralModal(false)}
              />
            }
            isOpen={true}
            onRequestClose={() => setReferralModal(false)}
          />
        )}
      </div>
    )
  );
};

export const SendDocumentForPatientToFill = ({ document, patient, openDocumentEdit }: any) => {
  const dispatch = useAppDispatch();
  const loading = useIsLoading(api.Documents.markDocumentPendingPatientFill);
  const [askPatientDialog, setAskPatientDialog] = useState(false);

  const canBeSentToPatientToFill = !!document.template_id;
  const isPending = document.pending_patient_fill ?? false;

  const alt = isPending ? 'Undo "Ask Patient to Fill"' : 'Ask Patient to Fill';

  const onClick = async (input: string) => {
    let fillAction = (Object.keys(DocumentUtils.OPTIONS_FILL).find(
      (key) => (DocumentUtils.OPTIONS_FILL as any)[key] === input,
    ) || 'nothing') as MarkDocumentPendingPatientFillFillActionEnum;

    await dispatch(
      api.Documents.markDocumentPendingPatientFill({
        patientId: patient.id,
        documentId: document.id,
        pending: !isPending,
        fillAction,
      }),
      {
        successTitle: isPending ? 'Fill Request Undone' : 'Fill Request Sent to Patient',
        errorTitle: isPending ? 'Error undoing request' : 'Error sending request to patient',
      },
    );
    openDocumentEdit(false);
  };

  if (!canBeSentToPatientToFill) {
    return null;
  }

  return (
    <div className="header-button">
      {askPatientDialog && (
        <ConfirmationDialog
          title={isPending ? 'Undo Request?' : 'Ask Patient to Fill?'}
          message={
            isPending ? (
              <>
                <p>The patient will no longer be able to fill the document</p>
                <p>Are you sure you want to proceed?</p>
              </>
            ) : (
              <>
                <p>The patient will receive an SMS being asked to fill the document</p>
                <p>Are you sure you want to proceed?</p>
              </>
            )
          }
          form={
            isPending
              ? undefined
              : [
                  {
                    type: 'select',
                    label: 'Action after patient fills',
                    inputs: Object.values(DocumentUtils.OPTIONS_FILL),
                    value: DocumentUtils.OPTIONS_FILL.nothing,
                  },
                ]
          }
          open={askPatientDialog}
          confirmLabel="Proceed"
          onConfirm={(_event: any, form: any) => {
            setAskPatientDialog(false);
            onClick(form['Action after patient fills']);
          }}
          onCancel={() => setAskPatientDialog(false)}
        />
      )}
      <MenuItem onClick={() => setAskPatientDialog(true)} style={{ opacity: isPending ? 0.75 : 1 }}>
        <ListItemIcon>
          <Icon
            className={isPending ? 'red' : undefined}
            src="user-pen"
            height="20px"
            width="20px"
            loading={loading}
          />
        </ListItemIcon>
        <ListItemText primary={alt} />
      </MenuItem>
    </div>
  );
};

export const SendDocument = ({ disableSend, document, patient }: any) => {
  const [sendModal, setSendModal] = useState(false);

  const emailBody =
    document.document_type === DocumentUtils.DOCUMENT_TYPE.appointment_notification
      ? `Hi,

Please see attached upcoming appointment information.

Thanks,
Care& Family Health`
      : undefined;

  const emailFirst =
    document.document_type === DocumentUtils.DOCUMENT_TYPE.appointment_notification;

  const emailSubject =
    document.document_type === DocumentUtils.DOCUMENT_TYPE.appointment_notification
      ? `Appointment Information`
      : undefined;

  return (
    !!patient &&
    !disableSend && (
      <div className="header-button">
        <IconButton onClick={() => setSendModal(true)}>
          <Icon
            src="paper-plane"
            alt="Send Document"
            showTooltip={true}
            height="20px"
            width="20px"
          />
        </IconButton>
        {sendModal && patient && (
          <DefaultModal
            wrap={true}
            title="Send Documents"
            className="large-modal send-multiple-documents-modal"
            padding={false}
            content={
              <SendMultipleDocuments
                patient={patient}
                onClose={() => setSendModal(false)}
                documents={[document]}
                /*@ts-ignore*/
                documentId={document.id}
                emailBody={emailBody}
                emailSubject={emailSubject}
                emailFirst={emailFirst}
                email={emailFirst ? patient.email : undefined}
              />
            }
            isOpen={true}
            onRequestClose={() => setSendModal(false)}
          />
        )}
      </div>
    )
  );
};

const EditPDFButton = (props: any) => {
  function openDocumentTemplateModal() {
    if (props.pinned) {
      let payload = {
        templateId: props.value.template_id,
        document: props.value,
        title: props.value.title,
      };

      props.dispatch(
        uiActions.addPinnedDocument({
          patientId: props.value.patient_id,
          type: 'template',
          id: props.value.template_id,
          payload,
        }),
      );
      props.dispatch(
        uiActions.removePinnedDocument({
          patientId: props.value.patient_id,
          id: props.value.id,
        }),
      );
    } else {
      props.setDocumentTemplateModal(true);
    }
  }

  return !!props.document.template_id ? (
    <>
      <div className="header-button">
        <IconButton onClick={openDocumentTemplateModal}>
          <Icon
            src="file-pdf"
            alt="Edit PDF"
            showTooltip={true}
            height="20px"
            width="20px"
            loading={props.updatingPdf}
          />
        </IconButton>
      </div>

      <DocumentTemplateModal {...props} onSave={props.updateDocumentPDF} onClose={props.onClose} />
    </>
  ) : null;
};

const SignPDFButton = (props: any) => {
  function openSignModal() {
    if (props.pinned) {
      let payload = {
        document: props.value,
        title: props.value.title,
      };
      props.dispatch(
        uiActions.removePinnedDocument({
          patientId: props.value.patient_id,
          id: props.value.id,
        }),
      );
      props.dispatch(
        uiActions.addPinnedDocument({
          patientId: props.value.patient_id,
          physicianId: props.value.physician_id,
          type: 'sign_document',
          id: props.value.id,
          payload,
        }),
      );
    } else {
      props.setSignModal(true);
    }
  }

  const hasFilename = !!props.document.filename;
  const hasPassword = !!props.document.has_password;
  const isPDF = hasFilename && props.document.filename.endsWith('.pdf');

  const shouldShowButton = hasFilename && isPDF;
  const isDisabled = hasPassword;

  return shouldShowButton ? (
    <div className="header-button">
      <IconButton onClick={isDisabled ? undefined : openSignModal} disabled={isDisabled}>
        <Icon
          src="file-signature"
          alt={isDisabled ? 'Cannot fill & sign password-protected documents' : 'Fill & Sign'}
          showTooltip={true}
          height="20px"
          width="20px"
          loading={props.updatingPdf}
          disabled={isDisabled}
        />
      </IconButton>
    </div>
  ) : null;
};

const SplitPDFButton = (props: any) => {
  return (
    <>
      {props.openDocumentEdit !== undefined && !props.hideEditDocument && (
        <>
          <MenuItem
            onClick={() => {
              props.openDocumentEdit(true);
              props.onMenuClose();
            }}
          >
            <ListItemIcon>
              <Icon src="clone" height="20px" />
            </ListItemIcon>
            <ListItemText primary="Delete, Split & Rotate" />
          </MenuItem>
        </>
      )}
    </>
  );
};

const ConvertToPDFButton = (props: any) => {
  const hasFilename = !!props.document.filename;
  const hasPassword = !!props.document.has_password;
  const isPDF = hasFilename && props.document.filename.endsWith('.pdf');
  const showConvertButton = hasFilename && !isPDF && !hasPassword;

  return showConvertButton ? (
    <MenuItem onClick={props.convertToPDF}>
      <ListItemIcon>
        <Icon src="file-image" height="20px" loading={props.updatingPdf} />
      </ListItemIcon>
      <ListItemText primary="Convert to PDF" />
    </MenuItem>
  ) : null;
};

export const DOCUMENT_TYPE_ARRAY = PerformanceUtils.objectToArray(DocumentUtils.DOCUMENT_TYPE);

const TypeField = ({
  value,
  updateDocumentFields,
  disableEdit,
  style = {},
  classificationResult,
}: any) => {
  function onChange(event: any) {
    let val = event;
    if (typeof val === 'object') {
      val = event.target.value;
    }
    updateDocumentFields({ document_type: val });
  }

  const suggestions = useMemo(() => {
    if (disableEdit) {
      return [];
    }

    const threshold = 0.5;
    return classificationResult?.classes?.filter((it: any) => it[1] >= threshold) ?? [];
  }, [disableEdit, classificationResult?.classes]);

  const inputElements = useMemo(() => {
    const dict: Record<string, number> = _.fromPairs(classificationResult?.classes ?? []);

    const values = _(_.clone(DOCUMENT_TYPE_ARRAY))
      .sortBy([(it: string) => 0 - (dict[it] ?? -1), _.identity])
      .map((it: string) => {
        return {
          value: it,
          certainty: dict[it],
        };
      })
      .value();

    return values.map((option: any) => {
      return (
        <MenuItem id={`menu-item-${option.value}`} value={option.value} key={option.value}>
          {RenderClassifierOption(option)}
        </MenuItem>
      );
    });
  }, [classificationResult]);

  const STYLE = { width: '100%', marginRight: '10px', ...style };

  return (
    <>
      <DefaultSelectField
        className="document-type-field"
        label="Type"
        value={value.document_type}
        disabled={disableEdit}
        onChange={onChange}
        inputElements={inputElements}
        style={STYLE}
      />
      {suggestions.length > 0 && (
        <ClassificationSuggestion suggestions={suggestions} onChange={onChange} />
      )}
    </>
  );
};

const NO_EDIT_DATE_STYLE = {
  width: '100%',
  marginRight: '10px',
};
const DateField = ({
  value,
  disableEdit,
  handleDateChange,
  style = {},
  updateDocumentFields,
  patient,
}: any) => (
  <>
    {disableEdit ? (
      <DefaultTextField
        name="date"
        placeholder="Date"
        style={{ ...NO_EDIT_DATE_STYLE, ...style }}
        value={CalendarUtils.format(value.date, dateFormat)}
        disabled={true}
      />
    ) : (
      <>
        <DefaultDatePicker
          name="date"
          key={value.date}
          value={value.date || moment().unix()}
          label="Date"
          style={{ ...NO_EDIT_DATE_STYLE, ...style }}
          disabled={false}
          onChange={(_: any, val: any) => handleDateChange('date', val)}
        />
        <RepeatField value={value} updateDocumentFields={updateDocumentFields} patient={patient} />
      </>
    )}
    {!!value.date_service && (
      <DefaultDatePicker
        key={value.date_service}
        name="date_service"
        value={value.date_service}
        label="Date of Service"
        onChange={(_: any, val: any) => handleDateChange('date_service', val)}
        style={{ ...NO_EDIT_DATE_STYLE, marginBottom: '10px', ...style }}
      />
    )}
  </>
);

export const RepeatField = ({
  value,
  updateDocumentFields,
  patient,
  includeStartDate = false,
}: any) => {
  const hasRepeat = !!value.repeat_options;

  if (!value.template_id) return null;

  const notSubscriber = !patient?.is_subscribed;

  return hasRepeat ? (
    <>
      {includeStartDate && (
        <div className="row">
          <DefaultDatePicker
            label="Start Date"
            value={value.date || moment().unix()}
            style={{
              width: '150px',
              display: 'flex',
              justifyContent: 'flex-end',
              marginRight: '10px',
            }}
            onChange={(_event: any, val: any) => {
              updateDocumentFields({
                date: val,
              });
            }}
            forceLabel
          />
        </div>
      )}
      <FormControl className="default-select-field document-repeat-fields" focused={true}>
        <InputLabel>Repeat Every</InputLabel>
        <div className="row">
          <DefaultSelectField
            id="repeat_options.number"
            style={{
              ...UIUtils.SELECT_FIELD_STYLE,
              marginTop: 0,
              width: '50px',
              marginRight: '10px',
            }}
            name="repeat_options.number"
            value={'' + value.repeat_options?.number}
            onChange={(event: any) => {
              updateDocumentFields({
                repeat_options: {
                  ...(value.repeat_options || {}),
                  number: parseInt(event.target.value),
                },
              });
            }}
            inputElements={Array.from(Array(24), (_, index) => index + 1).map((index) => (
              <MenuItem key={index} value={'' + index}>
                {index}
              </MenuItem>
            ))}
          />
          <DefaultSelectField
            id="repeat_options.unit"
            style={{
              ...UIUtils.SELECT_FIELD_STYLE,
              marginTop: 0,
              width: '110px',
              marginRight: '5px',
            }}
            name="repeat_options.unit"
            value={'' + value.repeat_options?.unit}
            onChange={(event: any) => {
              updateDocumentFields({
                repeat_options: {
                  ...(value.repeat_options || {}),
                  unit: event.target.value,
                },
              });
            }}
            inputElements={['Weeks', 'Months', 'Years'].map((index) => (
              <MenuItem key={index} value={'' + index}>
                {index}
              </MenuItem>
            ))}
          />
          <Icon
            src="circle-xmark"
            fontAwesomeStyle="fas"
            className="clear-icon"
            alt="Clear"
            showTooltip={true}
            style={{
              margin: 0,
              alignSelf: 'flex-end',
            }}
            onClick={() => {
              updateDocumentFields({
                repeat_options: undefined,
              });
            }}
          />
        </div>
      </FormControl>
      <DefaultTextField
        id="notification_text-field"
        style={{ width: '100%' }}
        name="notification_text"
        label="Patient Notification"
        placeholder="Your health records have been updated"
        value={value.notification_text || ''}
        inputLabelProps={{ shrink: true }}
        onChange={(event: any) => updateDocumentFields({ notification_text: event.target.value })}
      />
    </>
  ) : (
    <DefaultTooltip
      placement="top"
      title={notSubscriber ? 'Patient must be a subscriber' : undefined}
      arrow={true}
    >
      <span style={{ display: 'flex' }}>
        <DefaultSwitch
          label="Repeat Every"
          reversed={true}
          name="repeat_options"
          disabled={notSubscriber}
          style={{
            alignItems: 'flex-end',
          }}
          onChange={(_event: any, isInputChecked: boolean) => {
            if (isInputChecked) {
              updateDocumentFields({
                repeat_options: {},
              });
            }
          }}
          padded={false}
          checked={hasRepeat}
        />
      </span>
    </DefaultTooltip>
  );
};

const FromField = ({ value, updateDocumentFields, disableEdit }: any) => {
  function onChange(val: string) {
    updateDocumentFields({ from: val });
  }

  return (
    <div className="document-from" style={{ width: '100%', paddingRight: '20px' }}>
      {!disableEdit ? (
        <DefaultAutoComplete
          /*@ts-ignore*/
          style={{ width: '100%', marginTop: '5px' }}
          id="from-field"
          name="from"
          label="From"
          input={{
            value: value.from || '',
            onChange: (val: string) => onChange(val),
          }}
          onUpdateInput={(val: string) => onChange(val)}
          dataSource={DocumentUtils.DOCUMENT_FROM_OPTIONS}
        />
      ) : (
        <DefaultTextField
          style={{ width: '100%' }}
          id="from-field"
          name="from"
          label="From"
          value={value.from || ''}
          disabled={disableEdit}
        />
      )}
    </div>
  );
};

const TitleField = ({
  value,
  updateDocumentFields,
  disableEdit,
  style = {},
  classificationResult,
}: any) => {
  function onChange(val: string) {
    updateDocumentFields({ title: val });
  }

  const STYLE = { width: '100%', maxWidth: '100%', marginTop: '5px', ...style };
  const field = 'title';
  const label = 'Title';

  const REFERRAL_TYPES = [
    DocumentUtils.DOCUMENT_TYPE.referral,
    DocumentUtils.DOCUMENT_TYPE.consult_note,
  ];

  const replace = (text: string) => {
    if (text === 'PAP (Cytology)') return 'Pap';
    return text;
  };

  const titleSuggestions = useMemo(() => {
    const regularTitles =
      (value.document_type ? (DocumentUtils.DOCUMENT_TITLES as any)[value.document_type] : []) ??
      [];

    return [
      ..._(classificationResult?.titles ?? [])
        .map((it: any) => ({
          value: replace(it[0]?.trim()),
          certainty: it[1],
        }))
        .sortBy('certainty')
        .reverse()
        .uniqBy('value')
        .value(),
      ...regularTitles.map((val: string) => ({ value: val })),
    ].filter(({ value: v }: any) => !v.includes('New fax received'));
  }, [classificationResult, value.document_type]);

  if (REFERRAL_TYPES.includes(value.document_type) && !disableEdit)
    return (
      <ReferralTitle value={value} updateDocumentFields={updateDocumentFields} style={style} />
    );

  return (
    <div className="document-title">
      {!disableEdit ? (
        /*@ts-ignore*/
        <NewDefaultAutoComplete
          id="title-field"
          style={STYLE}
          /*@ts-ignore*/
          name={field}
          label={label}
          input={{
            value: value[field] || '',
            onChange: (val: string) => onChange(val),
          }}
          allowClear
          onUpdateInput={(val: string) => onChange(val)}
          options={titleSuggestions}
          renderOption={RenderClassifierOption}
        />
      ) : (
        <DefaultTextField
          id="title-field"
          name={field}
          label={label}
          placeholder={label}
          style={STYLE}
          value={value[field] || ''}
          disabled={disableEdit}
        />
      )}
    </div>
  );
};

const ReferralTitle = ({ value, updateDocumentFields, style = {} }: any) => {
  return (
    <div className="document-title">
      <DefaultTextField
        id="specialty-field"
        style={{ ...style, marginRight: '15px' }}
        name="referral_specialty"
        label="Specialty"
        value={value.referral_specialty || ''}
        onChange={(event: any) => updateDocumentFields({ referral_specialty: event.target.value })}
      />
      <DefaultTextField
        id="provider-field"
        style={style}
        name="referral_provider"
        label="Provider"
        value={value.referral_provider || ''}
        onChange={(event: any) => updateDocumentFields({ referral_provider: event.target.value })}
      />
    </div>
  );
};

const ArchiveToggle = ({ approve, value, updateDocumentFields, disableEdit }: any) => {
  function onChange(_event: any, isInputChecked: boolean) {
    updateDocumentFields({ archived: isInputChecked });
  }

  return (
    <div className="row">
      <DefaultSwitch
        reversed={true}
        key="archived"
        className="red"
        label="Archived"
        padded={false}
        disabled={approve || disableEdit}
        checked={value.archived === true}
        onChange={onChange}
      />
    </div>
  );
};

const TrackReferralToggle = ({
  approve,
  document,
  value,
  updateDocumentFields,
  disableEdit,
  ownId,
}: any) => {
  const [openActions, setOpenActions] = useState(false);

  if (value.document_type !== DocumentUtils.DOCUMENT_TYPE.referral) return null;

  function onTrack(_event: any, isInputChecked: boolean) {
    let comment = {
      author_id: ownId,
      text: `${isInputChecked ? 'Started' : 'Stopped'} tracking referral`,
      date: moment().unix(),
      read_by: [ownId],
    };

    let data: any = {
      referral_comments: [...(document.referral_comments || []), comment],
      referral_status: isInputChecked ? REFERRAL_STATUS.pending_send : undefined,
      date_check_referral: isInputChecked ? moment().add(21, 'days').unix() : undefined,
      referral_no_tracking: isInputChecked ? false : document.referral_no_tracking,
    };
    updateDocumentFields(data);
  }

  const isChecked = !!value.referral_status;

  return (
    <div className="hide-toggle row">
      <Switch
        key="track_referral"
        label="Track Referral"
        padded={false}
        reversed={true}
        disabled={approve || disableEdit === true}
        checked={isChecked}
        onChange={onTrack}
      />
      {document.referral_status && (
        <>
          <DefaultTextField
            name="referral_status"
            label="Referral Status"
            style={{ width: '40%' }}
            value={document.referral_status}
            disabled={true}
          />
          <IconButton
            onClick={() => setOpenActions(true)}
            style={{ width: '40px', height: '40px' }}
          >
            <>
              <Icon src="user-doctor" alt="Referral Tracking Details" showTooltip={true} />
              <UnreadBadge unreadCount={document.referral_comments?.length} />
            </>
          </IconButton>
          {openActions && (
            <DefaultModal
              className="fit-modal referral-actions-modal"
              title="Referral Actions"
              wrap={true}
              padding={false}
              content={<ReferralActions document={document} />}
              isOpen={openActions}
              onRequestClose={() => setOpenActions(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

const HideToggle = ({
  approve,
  value,
  updateDocumentFields,
  disableEdit,
  isPractitioner,
  isAdmin,
}: any) => {
  function onChange(_event: any, isInputChecked: boolean) {
    updateDocumentFields({ hide: isInputChecked });
  }

  return (
    <>
      <div className="hide-toggle">
        {isPractitioner && (
          <Switch
            key="hide"
            className="red"
            label="Hide"
            padded={false}
            reversed={true}
            disabled={approve || disableEdit === true || !isPractitioner}
            checked={value.hide === true}
            onChange={onChange}
          />
        )}
      </div>
      <div className="hide-toggle">
        {isAdmin && (
          <>
            <span className="hide-label">Hidden: {value.hide ? 'Yes' : 'No'}</span>
            {!!value.hide && (
              <Icon
                src="triangle-exclamation"
                className="red"
                alt="Document is hidden from patient"
                showTooltip={true}
                width="20px"
              />
            )}
            <br />
          </>
        )}
      </div>
      <div className="hide-toggle">
        {isAdmin && !value.approved && (
          <>
            <span className="approved-label">Approved: {value.approved ? 'Yes' : 'No'}</span>
            <Icon
              src="triangle-exclamation"
              className="red"
              alt="Document is hidden from patient"
              showTooltip={true}
              width="20px"
            />
          </>
        )}
      </div>
    </>
  );
};

const CommentField = ({
  value,
  isPractitioner,
  updateDocumentFields,
  isLocked = false,
  saveComment,
  deleteComment,
  submittingComment,
  ownId,
  ownPhysician,
}: any) => {
  function onChange(event: any) {
    updateDocumentFields({ [event.target.name]: event.target.value });
  }

  const isIntern = ownPhysician?.intern_of;
  const hasDiagnosticResults = value.results_text?.length > 0;

  return (
    <>
      {hasDiagnosticResults && (
        //@ts-expect-error
        <DiagnosticResults document={value} key={value.results_text?.length} />
      )}
      {!isIntern && (
        <div className="comment-field">
          <span className="small-title">Patient Notes</span>
          <TextareaAutosize
            placeholder="ATTENTION: Your note will be visible by the patient"
            cacheMeasurements
            name="comments"
            disabled={!isPractitioner || isLocked}
            value={value.comments}
            onChange={onChange}
          />
        </div>
      )}
      {/*@ts-ignore*/}
      <Comments
        value={value}
        saveComment={saveComment}
        deleteComment={deleteComment}
        submitting={submittingComment}
        ownId={ownId}
        commentsKey="general_comments"
        singleButton={true}
        title="Private Staff Comments"
      />
    </>
  );
};

const ApproveButtons = ({ loading, approveDocument, disableApprove, value }: any) => {
  function approve() {
    approveDocument(true);
  }

  function approveHide() {
    approveDocument(false);
  }

  return (
    !disableApprove && (
      <div className="button-wrapper row approve-row">
        <div className="inline-block">
          <DefaultButton
            icon="eye-slash"
            name="Approve and Hide"
            onClick={approve}
            disabled={loading || !value.patient_id}
          />
        </div>
        <div className="inline-block">
          <DefaultButton
            icon="eye"
            name="Approve and Show"
            onClick={approveHide}
            disabled={loading || !value.patient_id}
          />
        </div>
      </div>
    )
  );
};

const SaveButtons = ({
  loading,
  updatingPdf,
  hasDocumentChanged,
  resetDocument,
  disableEdit,
  updateDocument,
  alwaysAllowSave,
}: any) => {
  function onClickSave() {
    updateDocument();
  }

  return (
    !disableEdit && (
      <div>
        <div className="row">
          <div className="inline-block">
            <DefaultButton
              name="Cancel Changes"
              onClick={resetDocument}
              disabled={!hasDocumentChanged || loading || updatingPdf}
            />
          </div>
          <div className="inline-block">
            <DefaultButton
              name="Save Changes"
              className="blue"
              onClick={onClickSave}
              disabled={!alwaysAllowSave && (!hasDocumentChanged || updatingPdf)}
              loading={loading}
            />
          </div>
        </div>
      </div>
    )
  );
};

const LinkDocumentModal = ({
  updateDocumentFields,
  value,
  setLinkModal,
  linkModal,
  patient,
}: any) => {
  function closeModal() {
    setLinkModal(false);
  }

  function onSelectPatient(pat: any, selectedDocument: any) {
    closeModal();
    let patientId = pat ? pat.id : undefined;
    let linkedTo = selectedDocument ? selectedDocument.id : undefined;
    let linked_documents = value.linked_documents || DEFAULT_ARRAY;
    if (linkedTo) linked_documents = [...linked_documents, linkedTo];
    updateDocumentFields({
      patient_id: patientId,
      linked_documents: linked_documents,
    });
  }

  return (
    linkModal && (
      <DefaultModal
        key="link-document-modal"
        className="link-document-modal large-modal"
        wrap={!value.filename}
        padding={false}
        title={!value.filename ? 'Assign Patient' : undefined}
        content={
          <DocumentAssign
            document={value}
            onSelect={onSelectPatient}
            backButton={closeModal}
            patient={patient}
          />
        }
        isOpen={true}
        onRequestClose={closeModal}
      />
    )
  );
};

export const DocumentTemplateModal = ({
  patient,
  document,
  documentTemplateModal,
  setDocumentTemplateModal,
  onSave,
  templateId,
  physician,
  defaultSavedDocumentCategory,
  onClose,
  ...props
}: any) => {
  function closeModal(event?: any, shouldCloseEverything?: boolean) {
    setDocumentTemplateModal(false);
    if (shouldCloseEverything && !!onClose) onClose();
  }

  return (
    documentTemplateModal && (
      <DefaultModal
        className="large-modal"
        key="document-template-modal"
        onClose={closeModal}
        content={
          <DocumentTemplate
            key={document?.id}
            title={document?.title}
            templateId={templateId || document.template_id}
            document={document}
            onSave={onSave}
            onClose={closeModal}
            patient={patient}
            physician={physician}
            defaultSavedDocumentCategory={defaultSavedDocumentCategory}
            {...props}
          />
        }
        isOpen={documentTemplateModal}
        onRequestClose={closeModal}
      />
    )
  );
};

const LinkedDocumentBadge = ({ value, updateDocumentFields, setLinkModal }: any) => {
  const dispatch = useAppDispatch();
  const [documents, setDocuments] = useState<Record<string, any>>(DEFAULT_OBJECT);
  const [linkedModal, setLinkedModal] = useState<any>(false);
  const [loading, setLoading] = useState(false);
  const [failedDocumentIds, setFailedDocuments] = useState<string[]>([]);

  const { linked_documents = DEFAULT_ARRAY } = value;

  const missingDocuments = useMemo(() => {
    return linked_documents.filter(
      (id: string) => !documents[id] && !failedDocumentIds.includes(id),
    );
  }, [linked_documents, documents, failedDocumentIds]);

  useEffect(() => {
    if (loading) return;

    if (missingDocuments.length > 0) {
      setLoading(true);
      Promise.all(
        missingDocuments.map(async (id: string) => {
          const result = await dispatch(api.Documents.getDocument({ documentId: id }), {
            errorTitle: 'Get Linked Document',
          });
          if ((result as any)?.id) {
            return result;
          } else {
            setFailedDocuments((prevState) => [...prevState, id]);
            return undefined;
          }
        }),
      ).then((docs) => {
        let newDocuments: Record<string, any> = {};
        docs
          .filter((doc) => !!doc)
          .forEach((doc: any) => {
            newDocuments[doc.id] = doc;
          });
        if (Object.values(newDocuments).length > 0) {
          setDocuments(newDocuments);
        }
        setLoading(false);
      });
    }
  }, [missingDocuments, dispatch, loading]);

  function removeDocument(id: string) {
    let newLinked = [...linked_documents.filter((currentId: string) => currentId !== id)];
    updateDocumentFields({ linked_documents: newLinked });
  }

  return (
    <div className="linked-documents-section row">
      <div className="row">
        <span className="small-title">
          Linked Documents
          <Icon
            src="circle-plus"
            alt="Add Linked Document"
            showTooltip={true}
            width="15px"
            height="15px"
            onClick={() => setLinkModal(true)}
            className="inline-flex ml-2 relative"
            style={{ top: '1px' }}
          />
        </span>
      </div>
      {linked_documents.length > 0 && (
        <>
          <div className="row">
            {linked_documents.map((id: string) => {
              return (
                <div className="badge-wrapper" key={id}>
                  {loading && <ProgressMessage />}
                  {!!documents[id] && !!documents[id].id && (
                    <>
                      {/*@ts-ignore*/}
                      <DocumentBadge
                        item={documents[id]}
                        onClick={() => setLinkedModal(documents[id])}
                      />
                      <Icon
                        src="xmark"
                        alt="Clear Linked Document"
                        showTooltip={true}
                        width="15px"
                        onClick={() => removeDocument(id)}
                        className="clear-linked"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {!!linkedModal && (
            <DocumentDetailsModal
              document={linkedModal}
              onClose={() => setLinkedModal(undefined)}
              isOpen={!!linkedModal}
              title="Linked Document Details"
            />
          )}
        </>
      )}
    </div>
  );
};

const SignPdfModal = ({
  document,
  setSignModal,
  url,
  signModal,
  physician,
  patient,
  onClose,
  allowPin,
}: any) => {
  const dispatch = useAppDispatch();

  function closeModal() {
    setSignModal(false);
  }

  return (
    <ErrorBoundary>
      <DefaultModal
        onRequestClose={closeModal}
        className="large-modal"
        key="sign-document-modal"
        title="Fill & Sign"
        wrap={true}
        padding={false}
        onPin={
          allowPin && patient
            ? () => {
                dispatch(
                  uiActions.addPinnedDocument({
                    id: document.id,
                    type: 'sign_document',
                    patientId: document.patient_id,
                    physicianId: document.physician_id,
                    payload: {
                      //@ts-expect-error
                      document,
                      title: document.title,
                    },
                  }),
                );
                setSignModal(false);
                onClose();
              }
            : undefined
        }
        content={
          <SignDocument
            patient={patient}
            physician={physician}
            /*@ts-ignore*/
            templateId={document.template_id}
            onSign={closeModal}
            url={url}
            document={document}
            onClose={closeModal}
          />
        }
        isOpen={signModal}
      />
    </ErrorBoundary>
  );
};

export default DocumentDetailsView;

function getDocumentClassification(document: any) {
  try {
    const obj = JSON.parse(document?.classification ?? '');
    const classes: [string, number][] = obj.classes ?? [];
    const titles: [string, number][] = obj.titles ?? [];

    return {
      classes,
      titles,
    };
  } catch (err) {
    return { classes: [], titles: [] };
  }
}

function RenderClassifierOption(option: any) {
  const certaintyStr =
    (option.certainty && ` (${Math.floor(option.certainty * 100)}% match)`) || '';
  return (
    <div className="title-classification-option flex justify-between w-full">
      <div className="label">{option.value}</div>
      {option.certainty && (
        <div
          className="match px-1 rounded-md"
          style={{ color: getCertaintyColor(option.certainty) }}
        >
          {certaintyStr}
        </div>
      )}
    </div>
  );
}

function getCertaintyColor(certainty: number) {
  if (!certainty) {
    return;
  }
  if (certainty >= 0.8) {
    return '#2693d1';
  } else if (certainty >= 0.5) {
    return '#42bcdd';
  } else {
    return '#d16f3e';
  }
}

function ClassificationSuggestion({ suggestions, onChange }: any) {
  return (
    <div className="classification-suggestion-wrapper">
      {suggestions.map((it: any, idx: number) => {
        const [type, certainty] = it;
        return (
          <span className="classification-suggestion" key={idx} onClick={() => onChange(type)}>
            {type} ({Math.floor(certainty * 100)}% match)
          </span>
        );
      })}
    </div>
  );
}

function byteToMegabyte(bytes: number) {
  return (bytes / 1_000_000).toFixed(2);
}
